import { createContext, ReactNode, useEffect, useReducer } from 'react';
// utils
import axiosInstance, { PASSWORD_CHANGE_REQUIRED_EVENT } from '../utils/axios';
import { isValidToken, setSession, getAccessToken, clearSession } from '../utils/jwt';
import { AuthNotice, clearAuthNotice, setAuthNotice } from '../utils/authNotice';
// @types
import {
  ActionMap,
  AuthState,
  AuthUser,
  JWTContextType,
  LoginResult,
  StaffCodeResult,
} from '../@types/auth';
import { APIS } from '../utils/serviceUrls';

// ----------------------------------------------------------------------
// Phase 2.1: JWTContext rewritten to use axiosInstance everywhere,
// capture the refresh token from sign-in, and make logout() actually
// call the server (revoke the current session) instead of just clearing
// localStorage.
// ----------------------------------------------------------------------
// Only staff may use the admin app. Mirrors the BE STAFF_ROLE_VALUES
// (roles.decorator.ts): `partner` and `user` are deliberately absent. The API
// still authorises every call; this stops a learner or a partner from reaching
// the admin shell and account pages at all (LC-P16.1).
const STAFF_ROLES = [
  'admin',
  'super-admin',
  'support',
  'customer-care',
  'sales',
  'sales-manager',
  // Lead forms + leads inbox (SPEC H). Without it a marketing account is
  // refused at sign-in with NO_ADMIN_ACCESS_MESSAGE.
  'marketing',
  'instructor',
  'coordinator',
];

export const NO_ADMIN_ACCESS_MESSAGE = 'This account has no access to the Kayfa admin.';

function hasStaffRole(user: any): boolean {
  const roles: unknown[] = Array.isArray(user?.roles) ? user.roles : [];
  return roles.some((r) => STAFF_ROLES.includes(String(r)));
}

// Revoke THIS session on the server (so a learner does not lose one of their
// session slots to an admin sign-in that was refused), then forget it locally.
// Best-effort: a failed revoke still clears the local session.
async function endRefusedSession(): Promise<void> {
  try {
    await axiosInstance.delete(APIS.AUTH.SESSION_CURRENT);
  } catch {
    /* the local session is cleared either way */
  }
  clearSession();
}

// ----------------------------------------------------------------------

enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  Register = 'REGISTER',
  PasswordChangeRequired = 'PASSWORD_CHANGE_REQUIRED',
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
  [Types.Login]: {
    user: AuthUser;
  };
  [Types.Logout]: undefined;
  [Types.Register]: {
    user: AuthUser;
  };
  [Types.PasswordChangeRequired]: undefined;
};

export type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
      };
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
    case 'REGISTER':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };
    // The API refused a call with PASSWORD_CHANGE_REQUIRED: mark the user so
    // the guards send them to the change-password screen.
    case 'PASSWORD_CHANGE_REQUIRED':
      return state.user
        ? { ...state, user: { ...state.user, forcePasswordChange: true } }
        : state;
    default:
      return state;
  }
};

const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(JWTReducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = getAccessToken();
        if (accessToken && isValidToken(accessToken)) {
          // setSession syncs the axios default header for any subsequent
          // requests (initialization can race with first user navigation).
          setSession(accessToken);
          const response = await axiosInstance.get(APIS.AUTH.ME);
          const user = response.data.data;
          // A stored session of a non-staff account (for example one signed
          // in before this check existed) is ended, not resumed.
          if (!hasStaffRole(user)) {
            await endRefusedSession();
            dispatch({
              type: Types.Initial,
              payload: { isAuthenticated: false, user: null },
            });
            return;
          }
          dispatch({
            type: Types.Initial,
            payload: { isAuthenticated: true, user },
          });
        } else {
          dispatch({
            type: Types.Initial,
            payload: { isAuthenticated: false, user: null },
          });
        }
      } catch (err) {
        // /auth/me may 401 if the token is invalid — interceptor will
        // have attempted refresh; if we still got here, the session is
        // really gone.
        // eslint-disable-next-line no-console
        console.error('Auth initialization failed:', err);
        clearSession();
        dispatch({
          type: Types.Initial,
          payload: { isAuthenticated: false, user: null },
        });
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    const onPasswordChangeRequired = () => dispatch({ type: Types.PasswordChangeRequired });
    window.addEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, onPasswordChangeRequired);
    return () => window.removeEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, onPasswordChangeRequired);
  }, []);

  // Store the tokens of a successful sign-in, load the canonical user from
  // /auth/me and refuse a non-staff account. Shared by the (legacy) token
  // sign-in and the staff code step.
  const completeSignIn = async (data: any, extra: Record<string, unknown> = {}) => {
    const { accessToken, refreshToken, payload } = data;
    if (!accessToken) {
      throw new Error(data?.message || 'Sign-in response missing accessToken');
    }
    setSession(accessToken, refreshToken ?? null);

    // Fetch the canonical user shape from /auth/me so the UI uses the
    // same source of truth as a fresh page load.
    let user: AuthUser = payload ?? null;
    try {
      const me = await axiosInstance.get(APIS.AUTH.ME);
      user = me.data?.data ?? me.data ?? user;
    } catch {
      // Fall back to whatever the sign-in payload gave us.
    }

    // The credentials were right but the account is not staff (a learner, a
    // partner): end the session it just opened and say so on the form.
    if (!hasStaffRole(user)) {
      await endRefusedSession();
      dispatch({ type: Types.Logout });
      throw new Error(NO_ADMIN_ACCESS_MESSAGE);
    }

    clearAuthNotice();
    dispatch({ type: Types.Login, payload: { user: { ...user, ...extra } } });
  };

  // Phase 4b.4: login() accepts an optional 6-digit authenticator (TOTP) code.
  // The BE returns { mfaRequired: true } at HTTP 200 when MFA is enabled but
  // the code is missing; the form then asks for it and calls login() again.
  //
  // Staff sign-in code (auth/staff-otp): for every staff account the BE then
  // returns NO tokens but { otpRequired: true, channel, sentTo, ... } and has
  // sent a one-time code; verifyStaffCode() completes the sign-in. Calling
  // login() again with the same email + password sends a new code.
  const login = async (
    email: string,
    password: string,
    mfaCode?: string,
    otpChannel?: 'email' | 'whatsapp',
  ): Promise<LoginResult> => {
    const response = await axiosInstance.post(APIS.AUTH.SIGNIN, {
      email,
      password,
      // Phase 7.13: the BE now derives the sign-in surface from the user's
      // roles server-side, so the admin SPA no longer sends an `isAdmin` flag.
      ...(mfaCode ? { mfaCode } : {}),
      ...(otpChannel ? { otpChannel } : {}),
    });
    const data = response.data?.data ?? response.data;

    // MFA second-factor required — surface to the caller.
    if (data?.mfaRequired) {
      return { mfaRequired: true, email: data.email || email };
    }

    if (data?.otpRequired) {
      return {
        mfaRequired: false,
        otpRequired: true,
        channel: data.channel === 'whatsapp' ? 'whatsapp' : 'email',
        sentTo: typeof data.sentTo === 'string' ? data.sentTo : '',
        expiresInSeconds: Number(data.expiresInSeconds) || 600,
        resent: data.resent !== false,
        activation: data.activation === true,
        message: typeof data.message === 'string' ? data.message : '',
      };
    }

    await completeSignIn(data);
    return { mfaRequired: false };
  };

  // Staff sign-in, step 2: the code we emailed / sent on WhatsApp.
  const verifyStaffCode = async (email: string, otp: string): Promise<StaffCodeResult> => {
    const response = await axiosInstance.post(APIS.AUTH.VERIFY_OTP, { email, otp });
    const data = response.data?.data ?? response.data;

    if (data?.accessToken) {
      const forcePasswordChange = data.forcePasswordChange === true;
      await completeSignIn(data, forcePasswordChange ? { forcePasswordChange: true } : {});
      return { ok: true, forcePasswordChange };
    }

    const reason = data?.error === 'Account locked' ? 'locked' : data?.reason;
    return {
      ok: false,
      reason: reason === 'expired' || reason === 'too_many' || reason === 'locked' ? reason : 'invalid',
      message: data?.message || 'The code is not correct.',
    };
  };

  // Forget this session locally without calling the API (for when the server
  // already ended it, e.g. right after a password change) and leave a note
  // for the login page.
  const endSession = (notice?: AuthNotice) => {
    clearSession();
    if (notice) setAuthNotice(notice);
    dispatch({ type: Types.Logout });
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
  ) => {
    const response = await axiosInstance.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;
    setSession(accessToken);
    dispatch({ type: Types.Register, payload: { user } });
  };

  const logout = async () => {
    // Phase 7.14: revoke THIS device's current session server-side (DELETE
    // /v1/auth/sessions/current) so its access + refresh tokens are dead
    // immediately, not just cleared locally. (Previously this hit DELETE
    // /v1/auth/sessions = "revoke all OTHERS", which left the current session
    // alive. "Log out other devices" remains a separate Sessions-page action.)
    // Best-effort: if it fails (e.g. network down), still clear local state.
    try {
      await axiosInstance.delete(APIS.AUTH.SESSION_CURRENT);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Server logout failed; clearing local session anyway:', err);
    }
    clearSession();
    dispatch({ type: Types.Logout });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        mustChangePassword: state.isAuthenticated && state.user?.forcePasswordChange === true,
        login,
        verifyStaffCode,
        endSession,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
